import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Truck, RefreshCw, Shield, Minus, Plus, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 10: MONO
 * Style: Ultra-minimal, Clean, Typography-focused
 * Best for: Contemporary basics, Minimalist Indian wear, Premium essentials
 * Pure white theme with subtle gray accents
 */
const Template10 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // Categories - Minimal basics
    const categories = [
        { name: 'Kurtas', count: '48', image: 'https://placehold.co/600x800/F9FAFB/374151?text=Kurtas' },
        { name: 'Shirts', count: '36', image: 'https://placehold.co/600x800/F3F4F6/374151?text=Shirts' },
        { name: 'Linen', count: '24', image: 'https://placehold.co/600x800/E5E7EB/374151?text=Linen' }
    ];

    // Products - Minimalist Indian fashion
    const products = [
        { id: 1, name: 'Classic Cotton Kurta', price: '\u20B92,490', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Kurta+1' },
        { id: 2, name: 'Linen Blend Shirt', price: '\u20B91,890', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Shirt+1' },
        { id: 3, name: 'Pure Cotton Palazzo', price: '\u20B91,690', image: 'https://placehold.co/400x500/E5E7EB/374151?text=Palazzo+1' },
        { id: 4, name: 'Minimal Kurta Set', price: '\u20B93,290', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Set+1' },
        { id: 5, name: 'Handloom Cotton Saree', price: '\u20B94,990', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Saree+1' },
        { id: 6, name: 'Organic Linen Dress', price: '\u20B92,990', image: 'https://placehold.co/400x500/E5E7EB/374151?text=Dress+1' },
        { id: 7, name: 'Essential White Kurta', price: '\u20B91,990', image: 'https://placehold.co/400x500/F9FAFB/374151?text=Kurta+2' },
        { id: 8, name: 'Relaxed Fit Pants', price: '\u20B91,490', image: 'https://placehold.co/400x500/F3F4F6/374151?text=Pants+1' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'Finally, a brand that believes in less is more. The quality speaks for itself.', name: 'Aditi Sharma', location: 'Mumbai' },
        { text: 'Perfect everyday wear. Simple, comfortable, and beautifully made.', name: 'Vikram Iyer', location: 'Bangalore' }
    ];

    // Mobile testimonial auto-rotate
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ===== HERO SECTION ===== */}
            {/* Mobile Hero: stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-gray-50 flex items-center justify-center">
                    <div className="text-center px-6">
                        <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mb-4">
                            Mindful Essentials
                        </p>
                        <h1 className="text-4xl font-extralight text-gray-900 leading-[1.0] mb-4 tracking-tight">
                            Less,
                            <br />
                            but better.
                        </h1>
                        <p className="text-gray-500 text-[14px] leading-relaxed mb-6 max-w-xs mx-auto">
                            Thoughtfully designed clothing that simplifies your wardrobe. Quality over quantity. Always.
                        </p>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="w-full py-4 bg-gray-900 text-white text-sm font-medium tracking-wider uppercase flex items-center justify-center gap-3"
                        >
                            Explore Collection <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className="aspect-[16/9] overflow-hidden bg-gray-50">
                        <img
                            src="https://placehold.co/1400x600/F9FAFB/374151?text=Minimal+Collection"
                            alt="Collection"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Desktop Hero: Ultra Minimal - EXACT ORIGINAL */}
            <section className="hidden lg:flex min-h-[90vh] items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-8">
                        Mindful Essentials
                    </p>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extralight text-gray-900 leading-[1.0] mb-8 tracking-tight">
                        Less,
                        <br />
                        but better.
                    </h1>
                    <p className="text-gray-500 text-lg lg:text-xl max-w-xl mx-auto mb-12 leading-relaxed font-light">
                        Thoughtfully designed clothing that simplifies your wardrobe.
                        Quality over quantity. Always.
                    </p>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="group inline-flex items-center gap-4 text-gray-900 text-sm tracking-[0.2em] uppercase hover:gap-6 transition-all duration-300"
                    >
                        <span>Explore Collection</span>
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop Featured Image - EXACT ORIGINAL */}
            <section className="hidden lg:block px-4 sm:px-6 lg:px-8 pb-24">
                <div className="max-w-6xl mx-auto">
                    <div className="aspect-[16/9] lg:aspect-[21/9] overflow-hidden bg-gray-50">
                        <img
                            src="https://placehold.co/1400x600/F9FAFB/374151?text=Minimal+Collection"
                            alt="Collection"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* ===== PHILOSOPHY SECTION ===== */}
            <section className="py-16 lg:py-32 bg-gray-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-6">Our Philosophy</p>
                    <h2 className="text-2xl lg:text-4xl font-extralight text-gray-900 mb-8 leading-relaxed">
                        We believe in the power of simplicity.
                        Clothes that work harder so you don't have to think about them.
                    </h2>
                    <div className="h-px w-16 bg-gray-300 mx-auto" />
                </div>
            </section>

            {/* ===== CATEGORIES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <div className="flex justify-between items-end mb-5">
                        <div>
                            <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mb-1">Shop By</p>
                            <h2 className="text-xl font-extralight text-gray-900">Category</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="flex items-center gap-2 text-gray-500 text-xs tracking-wider uppercase"
                        >
                            View All <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                            {categories.map((category, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template10/shop')}
                                    className="flex-shrink-0 w-[68vw] snap-start cursor-pointer group"
                                >
                                    <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-3">
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                        <h3 className="text-gray-900 text-base font-light">{category.name}</h3>
                                        <span className="text-gray-400 text-xs">{category.count} items</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* Desktop: Categories - EXACT ORIGINAL */}
            <section className="hidden lg:block py-24 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-3">Shop By</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900">Category</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="group flex items-center gap-3 text-gray-500 text-sm tracking-wider uppercase hover:text-gray-900 transition-colors"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template10/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-6">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <div className="flex justify-between items-baseline">
                                    <h3 className="text-gray-900 text-lg font-light">{category.name}</h3>
                                    <span className="text-gray-400 text-sm">{category.count} items</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== PRODUCTS ===== */}
            {/* Mobile: horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 bg-gray-50">
                <div className="px-5">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mb-1">The Edit</p>
                            <h2 className="text-xl font-extralight text-gray-900">New Arrivals</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template10/shop')}
                                className="flex-shrink-0 w-[44vw] snap-start cursor-pointer group"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-2">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"
                                    >
                                        <Heart className={`w-4 h-4 ${wishlist[product.id] ? 'fill-gray-900 text-gray-900' : 'text-gray-500'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-md">
                                        <Plus className="w-4 h-4 text-gray-700" />
                                    </button>
                                </div>
                                <h3 className="text-gray-900 text-xs font-light line-clamp-1">{product.name}</h3>
                                <p className="text-gray-500 text-xs mt-0.5">{product.price}</p>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template10/shop')}
                        className="w-full mt-5 py-3.5 border border-gray-300 text-gray-700 text-sm font-medium"
                    >
                        Shop All
                    </button>
                </div>
            </section>

            {/* Desktop: Products grid - EXACT ORIGINAL */}
            <section className="hidden lg:block py-24 lg:py-32 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-end mb-16">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-3">The Edit</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900">New Arrivals</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template10/shop')}
                            className="group flex items-center gap-3 text-gray-500 text-sm tracking-wider uppercase hover:text-gray-900 transition-colors"
                        >
                            Shop All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 lg:gap-x-6 lg:gap-y-16">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template10/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                    <button className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Heart className="w-5 h-5 text-gray-600 hover:text-gray-900" />
                                    </button>
                                </div>
                                <h3 className="text-gray-900 font-light mb-1 group-hover:underline underline-offset-4 transition-all">
                                    {product.name}
                                </h3>
                                <p className="text-gray-500 text-sm">{product.price}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== QUOTE SECTION ===== */}
            <section className="py-16 lg:py-32 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <blockquote className="text-xl lg:text-3xl font-extralight text-gray-900 leading-relaxed mb-8">
                        "The ability to simplify means to eliminate the unnecessary
                        so that the necessary may speak."
                    </blockquote>
                    <cite className="text-gray-400 text-sm tracking-wider uppercase not-italic">
                        — Hans Hofmann
                    </cite>
                </div>
            </section>

            {/* ===== MATERIALS SECTION ===== */}
            {/* Mobile: full-bleed stacked */}
            <section className="lg:hidden py-10 bg-gray-50">
                <div>
                    <img
                        src="https://placehold.co/400x500/F9FAFB/374151?text=Fabric+1"
                        alt="Fabric"
                        className="w-full aspect-[4/5] object-cover"
                    />
                </div>
                <div className="px-5 py-8">
                    <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mb-3">Our Materials</p>
                    <h2 className="text-xl font-extralight text-gray-900 mb-4 leading-snug">
                        Honest fabrics,
                        <br />
                        no compromises.
                    </h2>
                    <p className="text-gray-600 text-[14px] leading-relaxed mb-4">
                        We source only natural, breathable fabrics. Pure cotton from Maharashtra,
                        handwoven linen from Bengal, and organic khadi from Gujarat.
                        Each piece is made to last, designed to age beautifully.
                    </p>
                    <div className="space-y-3">
                        {['100% Natural Fibers', 'OEKO-TEX Certified', 'Handcrafted in India', 'Zero Synthetic Blends'].map((item, index) => (
                            <div key={index} className="flex items-center gap-3">
                                <div className="w-1 h-1 bg-gray-400 rounded-full" />
                                <span className="text-gray-700 text-sm">{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: Materials - EXACT ORIGINAL */}
            <section className="hidden lg:block py-24 lg:py-32 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                        <div>
                            <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-6">Our Materials</p>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-gray-900 mb-8">
                                Honest fabrics,
                                <br />
                                no compromises.
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                We source only natural, breathable fabrics. Pure cotton from Maharashtra,
                                handwoven linen from Bengal, and organic khadi from Gujarat.
                                Each piece is made to last, designed to age beautifully.
                            </p>
                            <div className="space-y-4">
                                {['100% Natural Fibers', 'OEKO-TEX Certified', 'Handcrafted in India', 'Zero Synthetic Blends'].map((item, index) => (
                                    <div key={index} className="flex items-center gap-4">
                                        <div className="w-1 h-1 bg-gray-400 rounded-full" />
                                        <span className="text-gray-700 text-sm">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="aspect-[3/4] overflow-hidden bg-gray-100">
                                <img
                                    src="https://placehold.co/400x500/F9FAFB/374151?text=Fabric+1"
                                    alt="Fabric"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="aspect-[3/4] overflow-hidden bg-gray-100 mt-8">
                                <img
                                    src="https://placehold.co/400x500/F3F4F6/374151?text=Fabric+2"
                                    alt="Fabric"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== TESTIMONIALS ===== */}
            {/* Mobile: swipeable carousel */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <p className="text-gray-400 text-xs tracking-[0.3em] uppercase mb-5 text-center">What People Say</p>
                    <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div className="text-center">
                                        <p className="text-lg font-extralight text-gray-900 leading-relaxed mb-5 italic">
                                            "{testimonial.text}"
                                        </p>
                                        <div>
                                            <p className="text-gray-900 text-sm font-medium">{testimonial.name}</p>
                                            <p className="text-gray-400 text-xs">{testimonial.location}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-5">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className={`h-2 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-gray-900' : 'w-2 bg-gray-300'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: Testimonials - EXACT ORIGINAL */}
            <section className="hidden lg:block py-24 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p className="text-gray-400 text-sm tracking-[0.3em] uppercase mb-16 text-center">What People Say</p>
                    <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="text-center">
                                <p className="text-xl lg:text-2xl font-extralight text-gray-900 mb-8 leading-relaxed">
                                    "{testimonial.text}"
                                </p>
                                <div>
                                    <p className="text-gray-900 text-sm font-medium">{testimonial.name}</p>
                                    <p className="text-gray-400 text-sm">{testimonial.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== NEWSLETTER ===== */}
            {/* Mobile: stacked rounded */}
            <section className="lg:hidden py-10 bg-gray-900 text-white">
                <div className="px-5 text-center">
                    <p className="text-gray-500 text-xs tracking-[0.3em] uppercase mb-3">Newsletter</p>
                    <h2 className="text-2xl font-extralight text-white mb-3">Stay in the loop</h2>
                    <p className="text-gray-400 text-[14px] mb-6">New arrivals. Behind the scenes. No spam.</p>
                    <div className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                            type="email"
                            placeholder="Your email"
                            className="px-5 py-3.5 bg-transparent border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors text-sm"
                        />
                        <button className="w-full px-8 py-3.5 bg-white text-gray-900 text-sm tracking-wider uppercase rounded-xl">
                            Join
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop: Newsletter - EXACT ORIGINAL */}
            <section className="hidden lg:block py-24 lg:py-32 bg-gray-900 text-white">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-gray-500 text-sm tracking-[0.3em] uppercase mb-6">Newsletter</p>
                    <h2 className="text-3xl lg:text-4xl font-extralight text-white mb-6">
                        Stay in the loop
                    </h2>
                    <p className="text-gray-400 mb-10">
                        New arrivals. Behind the scenes. No spam.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email"
                            className="flex-1 px-6 py-4 bg-transparent border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-white text-gray-900 text-sm tracking-wider uppercase hover:bg-gray-100 transition-colors">
                            Join
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== INSTAGRAM ===== */}
            <section className="py-16 lg:py-32 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10 lg:mb-16">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 text-sm tracking-wider">@mono.essentials</span>
                        </div>
                    </div>

                    {/* Mobile: 3-col tight grid */}
                    <div className="grid grid-cols-3 gap-0.5 lg:hidden">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="aspect-square overflow-hidden bg-gray-100">
                                <img
                                    src={`https://placehold.co/300x300/F3F4F6/374151?text=${index + 1}`}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>
                    {/* Desktop: 6-col */}
                    <div className="hidden lg:grid grid-cols-4 md:grid-cols-6 gap-2">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="aspect-square overflow-hidden bg-gray-100 cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/F3F4F6/374151?text=${index + 1}`}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SERVICES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-8 border-t border-gray-100">
                <div className="px-5">
                    <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'Orders above \u20B91,499' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '30-day return window' },
                            { icon: Shield, title: 'Quality Guarantee', desc: 'Lifetime warranty' },
                            { icon: Heart, title: 'Made with Care', desc: 'Ethical production' },
                        ].map((service, i) => (
                            <div key={i} className="flex-shrink-0 w-[55vw] snap-start text-center py-4">
                                <service.icon className="w-5 h-5 text-gray-400 mx-auto mb-3" />
                                <p className="text-gray-900 text-sm font-medium mb-1">{service.title}</p>
                                <p className="text-gray-400 text-xs">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: Services - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
                        <div>
                            <Truck className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Free Shipping</p>
                            <p className="text-gray-400 text-xs">Orders above {'\u20B9'}1,499</p>
                        </div>
                        <div>
                            <RefreshCw className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Easy Returns</p>
                            <p className="text-gray-400 text-xs">30-day return window</p>
                        </div>
                        <div>
                            <Shield className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Quality Guarantee</p>
                            <p className="text-gray-400 text-xs">Lifetime warranty</p>
                        </div>
                        <div>
                            <Heart className="w-5 h-5 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-900 text-sm font-medium mb-1">Made with Care</p>
                            <p className="text-gray-400 text-xs">Ethical production</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template10;
